import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import ReportActions from '../../Components/ReportActions';
import './CandidateReport.css';
import './CandidateReportPrint.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.avtechbmt.cloud/api/candidates';

const FRIENDLY_LABELS = {
  Understanding: 'Understanding',
  coherence: 'Coherence',
  connection: 'Connection',
  clarification: 'Clarification',
  concision: 'Concision',
  conscious: 'Awareness',
  confidence: 'Confidence',
};

const TAILORED_DIAGNOSTICS = {
  Understanding: {
    critical: "🔴 CRITICAL RESTRUCTURING REQUIRED: Misses underlying nuances. Focus on pausing completely and processing the interviewer's baseline intent before jumping to structural mapping.",
    warning: '🟡 OPTIMIZATION AREA: Good general understanding, but tends to overlook hidden complexities. Work on verifying the deeper requirements of multi-layered questions.',
    success: '🟢 COMPETITIVE STRENGTH: Exceptional parsing of complex requirements. Proactively neutralizes ambiguity and maps out solutions accurately.',
  },
  coherence: {
    critical: '🔴 CRITICAL RESTRUCTURING REQUIRED: Responses feel like fragmented project logs or unorganized timelines. Seamlessly tie your experience back to core metrics and values.',
    warning: '🟡 OPTIMIZATION AREA: Safe narrative flow, but lacks a clean bridge between past life lessons and current technical problem-solving frameworks.',
    success: '🟢 COMPETITIVE STRENGTH: Beautifully synthesized storytelling. Connects metrics, engineering challenges, and personal values seamlessly.',
  },
  connection: {
    critical: '🔴 CRITICAL RESTRUCTURING REQUIRED: Demonstrates visible anxiety barriers or low early engagement. Practice positive verbal affirmations and relaxed eye contact.',
    warning: '🟡 OPTIMIZATION AREA: Professional but slightly rigid. Establish conversational comfort thresholds earlier via subtle behavioral mirroring.',
    success: '🟢 COMPETITIVE STRENGTH: Superb interpersonal instincts. Instantly reads environmental cues and builds organic professional rapport.',
  },
  clarification: {
    critical: "🔴 CRITICAL RESTRUCTURING REQUIRED: Dangerously prone to rushing into blind programming assumptions. Adopt explicit framing validation strings like 'So what you are asking is...'.",
    warning: '🟡 OPTIMIZATION AREA: Tends to ask superficial follow-up questions. Push for strategic alignment on scope limits before writing or designing.',
    success: '🟢 COMPETITIVE STRENGTH: Excellent safety mechanisms. Locks down constraints and clarifies edge cases before making costly engineering trade-offs.',
  },
  concision: {
    critical: '🔴 CRITICAL RESTRUCTURING REQUIRED: Explanations are causing cognitive layout shifting or panel fatigue. Hard-limit responses to 30–120 seconds max.',
    warning: '🟡 OPTIMIZATION AREA: Answers are informative but over-indexed on background context. Condense the setup to leave more room for high-impact takeaways.',
    success: '🟢 COMPETITIVE STRENGTH: Masterfully brief yet impactful. Delivers punchy answers well within conversational attention windows.',
  },
  conscious: {
    critical: "🔴 CRITICAL RESTRUCTURING REQUIRED: Appears disconnected from the panel's micro-expressions. Actively work on calming systemic nervous reflexes to read team energy.",
    warning: '🟡 OPTIMIZATION AREA: Steady performance, but misses opportunities to adapt tone, pace, or technical depth to the exact profile of the evaluating group.',
    success: '🟢 COMPETITIVE STRENGTH: Elite environmental awareness. Perfectly matches team tonality while guiding complex technical dialogues smoothly.',
  },
  confidence: {
    critical: "🔴 CRITICAL RESTRUCTURING REQUIRED: Either shifts entirely into individualistic over-inflation ('I did it all') or underrepresents technical capability out of modesty.",
    warning: "🟡 OPTIMIZATION AREA: Competent delivery, but needs to dial up team-first 'we' frameworks to demonstrate scalable leadership alongside technical authority.",
    success: '🟢 COMPETITIVE STRENGTH: Outstanding, balanced assertiveness. Projects deep architectural ownership while celebrating collaborative triumphs.',
  },
};

const toneOf = (v) => (v <= 2 ? 'critical' : v === 3 ? 'warning' : 'success');
const toneLabel = (v) =>
  v <= 2 ? 'Critical — Restructure' : v === 3 ? 'Warning — Optimize' : 'Strength — Ready';

export default function CandidateReport({ candidateId, reportId, onBack }) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [report, setReport] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const activeToken = token || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
      };

      const res = await fetch(`${API_BASE_URL}/reports/${reportId}`, { headers });
      const text = await res.text();
      const data = JSON.parse(text);

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to load report');
      }

      const rpt = data.data;
      setReport(rpt);
      setCandidate(rpt.candidate || { _id: candidateId, name: 'Candidate' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [reportId, candidateId, token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) {
    return (
      <div className="report-deep-dive">
        <div style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
          Loading report…
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-error-state">
        <h2>⚠️ Report could not be loaded.</h2>
        <p>{error || 'Report not found.'}</p>
        {onBack && (
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            ← Back to Reports
          </button>
        )}
      </div>
    );
  }

  const studentName = candidate?.name || 'Unnamed Candidate';

  const scores = report.scores || {
    Understanding: 0,
    coherence: 0,
    connection: 0,
    clarification: 0,
    concision: 0,
    conscious: 0,
    confidence: 0,
  };

  const criteriaEntries = Object.entries(scores);
  const totalScore = criteriaEntries.reduce((a, [, v]) => a + Number(v || 0), 0);
  const averageScore =
    report.overallScore !== undefined
      ? Number(report.overallScore).toFixed(1)
      : (totalScore / criteriaEntries.length).toFixed(1);

  const criticalCount = criteriaEntries.filter(([, v]) => Number(v) <= 2).length;
  const warningCount = criteriaEntries.filter(([, v]) => Number(v) === 3).length;
  const strengthCount = criteriaEntries.filter(([, v]) => Number(v) >= 4).length;

  const overallTone =
    Number(averageScore) >= 4 ? 'success' : Number(averageScore) >= 3 ? 'warning' : 'critical';
  const overallVerdict =
    overallTone === 'success'
      ? 'Strong competitive profile — ready for final rounds.'
      : overallTone === 'warning'
      ? 'Promising profile — target specific optimisation areas.'
      : 'Significant restructuring required before proceeding.';

  /* --- Radar chart math --- */
  const width = 300;
  const height = 300;
  const cx = width / 2;
  const cy = height / 2;
  const r = 100;

  const webLevels = [1, 2, 3, 4, 5];
  const webGridPoints = webLevels.map((level) => {
    const radius = (level / 5) * r;
    return criteriaEntries
      .map((_, i) => {
        const angle = (i * 2 * Math.PI) / 7 - Math.PI / 2;
        return `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
      })
      .join(' ');
  });

  const candidatePoints = criteriaEntries
    .map(([, val], i) => {
      const radius = (Math.max(Number(val) || 0, 0.2) / 5) * r;
      const angle = (i * 2 * Math.PI) / 7 - Math.PI / 2;
      return `${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`;
    })
    .join(' ');

  const labelPositions = criteriaEntries.map(([key], i) => {
    const angle = (i * 2 * Math.PI) / 7 - Math.PI / 2;
    return {
      x: cx + r * 1.3 * Math.cos(angle),
      y: cy + r * 1.3 * Math.sin(angle),
      label: FRIENDLY_LABELS[key] || key,
    };
  });

  return (
    <div className="report-deep-dive" ref={reportRef}>
      <ReportActions reportRef={reportRef} studentName={studentName} onBack={onBack} />

      <div className="report-page">
        {/* ==================== PAGE 1 ==================== */}
        <div className="pdf-page page-1">
          <div className="print-only-branding">
            <div className="print-logo-container">
              <img
                src="/avtech-logo.jpeg"
                alt="AV Tech Logo"
                className="print-company-logo"
                style={{ height: '10mm', width: 'auto', objectFit: 'contain' }}
              />
              <span className="print-company-name">
                AVTECH BARAMATI PRIVATE LIMITED
              </span>
            </div>
            <div className="print-timestamp">
              Report Generated: {new Date().toLocaleDateString()}
            </div>
          </div>

          <header className="report-hero">
            <div className="report-hero-identity">
              <div className="report-hero-avatar" aria-hidden="true">
                {studentName.charAt(0).toUpperCase()}
              </div>
              <div className="report-hero-text">
                <p className="report-eyebrow">Candidate Performance Diagnostics</p>
                <h2 className="report-name">{studentName}</h2>
                <p className="report-subtitle">
                  Evaluation Session · {new Date(report.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="report-score-summary">
              <div className="report-score-block">
                <span className="report-score-num">{totalScore}</span>
                <span className="report-score-label">/ 35 Total</span>
              </div>
              <div className={`report-score-block accent tone-${overallTone}`}>
                <span className="report-score-num">{averageScore}</span>
                <span className="report-score-label">/ 5 Average</span>
              </div>
            </div>
          </header>

          <section className={`report-verdict tone-${overallTone}`}>
            <div className="report-verdict-badge">
              {overallTone === 'success'
                ? '🟢 READY'
                : overallTone === 'warning'
                ? '🟡 OPTIMIZE'
                : '🔴 RESTRUCTURE'}
            </div>
            <div className="report-verdict-text">
              <strong>Overall Assessment:</strong> {overallVerdict}
            </div>
            <div className="report-verdict-stats">
              <span className="rv-stat critical">
                <b>{criticalCount}</b> Critical
              </span>
              <span className="rv-stat warning">
                <b>{warningCount}</b> Optimize
              </span>
              <span className="rv-stat success">
                <b>{strengthCount}</b> Strength
              </span>
            </div>
          </section>

          <nav className="report-tabs no-print" role="tablist">
            {[
              { id: 'overview', label: '📊 Overview' },
              { id: 'breakdown', label: '🔍 Breakdown' },
              { id: 'actions', label: '🎯 Action Plan' },
            ].map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                className={`report-tab ${activeTab === t.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <section
            className={`report-section report-section-overview ${
              activeTab === 'overview' ? '' : 'screen-hidden'
            }`}
          >
            <div className="print-section-divider">
              <span>Section 1 · Performance Overview</span>
            </div>
            <div className="charts-flex-container">
              <div className="chart-wrapper-card">
                <h4 className="chart-title">Performance Radar Matrix</h4>
                <div className="radar-chart-container">
                  <svg
                    viewBox={`0 0 ${width} ${height}`}
                    className="radar-svg"
                    preserveAspectRatio="xMidYMid meet"
                    width="100%"
                    height="auto"
                  >
                    {webGridPoints.map((pointsStr, idx) => (
                      <polygon key={idx} points={pointsStr} className="radar-grid-line" />
                    ))}
                    {criteriaEntries.map((_, i) => {
                      const angle = (i * 2 * Math.PI) / 7 - Math.PI / 2;
                      return (
                        <line
                          key={i}
                          x1={cx}
                          y1={cy}
                          x2={cx + r * Math.cos(angle)}
                          y2={cy + r * Math.sin(angle)}
                          className="radar-axis-line"
                        />
                      );
                    })}
                    <polygon points={candidatePoints} className="radar-candidate-area" />
                    {candidatePoints.split(' ').map((pt, idx) => {
                      const [x, y] = pt.split(',');
                      return (
                        <circle
                          key={idx}
                          cx={x}
                          cy={y}
                          r="4.5"
                          className="radar-candidate-node"
                        />
                      );
                    })}
                    {labelPositions.map((pos, idx) => (
                      <text
                        key={idx}
                        x={pos.x}
                        y={pos.y}
                        className="radar-label"
                        textAnchor="middle"
                      >
                        {pos.label}
                      </text>
                    ))}
                  </svg>
                </div>
              </div>

              <div className="chart-wrapper-card bar-chart-card">
                <h4 className="chart-title">Volumetric Distribution</h4>
                <div className="pdf-bar-container">
                  {criteriaEntries.map(([key, val]) => {
                    const n = Number(val) || 0;
                    return (
                      <div key={key} className="pdf-bar-item">
                        <span className="pdf-bar-label">{FRIENDLY_LABELS[key]}</span>
                        <div className="pdf-bar-track">
                          <div
                            className={`pdf-bar-fill tone-${toneOf(n)}`}
                            style={{ width: `${(n / 5) * 100}%` }}
                          />
                        </div>
                        <span className="pdf-bar-value">{n} / 5</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="report-legend">
              <span className="report-legend-title">Score Interpretation</span>
              <span className="report-legend-item tone-success">🟢 4–5 · Strength</span>
              <span className="report-legend-item tone-warning">🟡 3 · Optimize</span>
              <span className="report-legend-item tone-critical">🔴 1–2 · Restructure</span>
            </div>
          </section>
        </div>

        {/* ==================== PAGE 2 ==================== */}
        <div className="pdf-page page-2">
          <section
            className={`report-section report-section-breakdown ${
              activeTab === 'breakdown' ? '' : 'screen-hidden'
            }`}
          >
            <div className="print-section-divider">
              <span>Section 2 · Detailed Criteria Breakdown</span>
            </div>
            <h3 className="report-section-heading">Detailed Criteria Breakdown</h3>
            <div className="criteria-table">
              {criteriaEntries.map(([key, val]) => {
                const n = Number(val) || 0;
                const tone = toneOf(n);
                return (
                  <article key={key} className={`criteria-row tone-${tone}`}>
                    <div className="criteria-row-head">
                      <h5>{FRIENDLY_LABELS[key]}</h5>
                      <span className={`criteria-score tone-${tone}`}>{n} / 5</span>
                    </div>
                    <p className="criteria-advice">
                      {TAILORED_DIAGNOSTICS[key]?.[tone] || ''}
                    </p>
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        {/* ==================== PAGE 3 ==================== */}
        <div className="pdf-page page-3">
          <section
            className={`report-section report-section-actions ${
              activeTab === 'actions' ? '' : 'screen-hidden'
            }`}
          >
            <div className="print-section-divider">
              <span>Section 3 · Personal Diagnostic Consultation</span>
            </div>
            <h3 className="report-section-heading">
              🎯 Personal Diagnostic Consultation &amp; Action Plan
            </h3>
            <div className="advice-grid">
              {criteriaEntries.map(([key, val]) => {
                const n = Number(val) || 0;
                const tone = toneOf(n);
                return (
                  <div key={key} className={`advice-card-item advice-${tone}`}>
                    <h5 className="advice-card-title">
                      {FRIENDLY_LABELS[key]}
                      <span className="advice-card-score">
                        {n}/5 · {toneLabel(n)}
                      </span>
                    </h5>
                    <p className="advice-card-text">
                      {TAILORED_DIAGNOSTICS[key]?.[tone] || ''}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <footer className="print-footer">
            AVTECH Baramati Pvt. Ltd. · Confidential Candidate Assessment ·
            Generated {new Date().toLocaleDateString()}
          </footer>
        </div>
      </div>
    </div>
  );
}